/* eslint-disable object-curly-newline,no-param-reassign,no-unused-expressions,
no-case-declarations */
/* global G */
import asObject from 'lib/sequence/component/children/asObject'
import validate from 'lib/sequence/model/validate'
import validateStepperFn from 'app/_shared/component/stepper/validate'
import { asyncPipeSpread, asyncPipeSpreadIf, deleteKey } from 'lib/util'
import find from 'lib/sequence/component/children/find'
import { hidden } from 'lib/sequence/component/state/hidden'
import { checked } from 'lib/sequence/component/state/checked'
import { disable, enable } from 'lib/sequence/component/state/disabled'
import { setData } from 'lib/sequence/model/api/set'


/**
 * Removes any previous module and model errors.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(...[*]): Promise<*[]>}
 */
const purgeError = module => async (...args) => {
  const model = module[G.MODEL]
  deleteKey(G.ERROR, module[G.STATE])
  deleteKey(G.ERROR, model[G.STATE])
  return args
}

/**
 * Sets the organisation type to "customer" if it is not already set.
 *
 * The organisation may have no type at this point if the user wasn't allowed to select it.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @return {function(...[*]): Promise<*[]>}
 */
const setOrganisationType = module => async (...args) => {
  const model = module[G.MODEL]
  const { organisation } = model[G.CHILDREN]

  organisation[G.STATE][G.BULK]
    && !organisation[G.DATA].type
    && setData(organisation, { type: 'customer' })

  return args
}

/**
 * Checks that all required steps have completed and confirmed states and sets them all as visited.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*, ...[*]): Promise<*[]>}
 */
const validateStepper = module => async (components, ...args) => {
  const result = validateStepperFn(module)
  return [components, result, ...args]
}

/**
 * Validates the contact step against the model and its requesterContactData attribute.
 *
 * @param {Gaia.AppModule.Spec} module  the current module
 * @returns {function(*, *, ...[*]): Promise<*[]>}
 */
const validateContactStep = module => async (components, stepperValid, ...args) => {
  const model = module[G.MODEL]
  const { invite, contactChannels } = model[G.CHILDREN]
  const { email } = contactChannels[G.CHILDREN]
  const { stepper, toBeInvited } = components
  const { contact: step } = asObject(stepper[G.CHILDREN])
  const { form, inviteForm } = find(step)
  const invited = checked(toBeInvited)

  const notEmptyValidator = email[G.VALIDATOR].find(validator => validator._name === 'notEmpty')

  !invited
    && disable(contactChannels[G.VALIDATOR].email)
    && disable(notEmptyValidator)

  await validate(model)(form)

  invited
    ? setData(model, { invite: { status: 'to_be_invited', role: 'customer' } })
      && validate(invite)(inviteForm)
    : setData(model, { invite: null })

  enable(contactChannels[G.VALIDATOR].email)
  enable(notEmptyValidator)


  const modelValid = !model[G.STATE][G.ERROR]

  module[G.STATE][G.ERROR] = stepperValid && modelValid

  return [components, ...args]
}

/**
 * Checks whether the currently inserted ticket data is valid and all required steps are completed.
 * If that's the case, finishes filling the ticket with data that can be deduced.
 *
 * @param {Gaia.AppModule.Spec} module the current module composition object
 * @returns {function(): function(...[*]): Promise<*>}
 */
const bulk = module => () => async (...args) => asyncPipeSpread(
  purgeError(module),
  setOrganisationType(module),
  validateStepper(module),
  validateContactStep(module),
)(find(module[G.STATE][G.ACTION][G.COMPONENT]), ...args)

export default bulk
